import React, { useCallback, useEffect, useState } from 'react';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import { friendlyCheckinError, loadPasses, replacePass } from './checkinApi';
import { PassCard, PassPrintSheet, downloadPass, printPasses, usePrintCleanup } from './PassCards';

// Module 19 (BL-380) G5 - "QR pass" for one guest (show, print, download, replace) and "QR passes" for the whole event (print the sheet).
// The passes come from the checked m19_checkin_passes() function (Events edit); a missing pass is made on the way. Nothing is sent to a guest:
// the guest's own page that will show the pass is G4.

function usePasses(client, engagementId, guestId) {
    const [state, setState] = useState({ loading: true, error: '', data: null });
    const load = useCallback(async () => {
        setState((previous) => ({ ...previous, loading: true, error: '' }));
        try {
            const data = await loadPasses(client, engagementId, guestId);
            setState({ loading: false, error: '', data });
        } catch (error) {
            setState({ loading: false, error: friendlyCheckinError(error), data: null });
        }
    }, [client, engagementId, guestId]);
    useEffect(() => { load(); }, [load]);
    return [state, setState, load];
}

export function GuestPassPopup({ client, engagementId, guest, canReplace = true, onClose }) {
    usePrintCleanup();
    const [state, setState] = usePasses(client, engagementId, guest.id);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const pass = state.data?.passes?.[0] || null;

    const replace = async () => {
        setBusy(true); setNotice('');
        try {
            const fresh = await replacePass(client, engagementId, guest.id);
            setState((previous) => ({ ...previous, data: { ...previous.data, passes: [{ ...pass, token: fresh.token }] } }));
            setNotice('New pass made. The old pass no longer works at the door.');
            setConfirming(false);
        } catch (error) {
            setState((previous) => ({ ...previous, error: friendlyCheckinError(error) }));
        }
        setBusy(false);
    };

    return (
        <Modal3D
            portal
            title="QR pass"
            subtitle="Show it, print it or save it. The code holds no name or contact details."
            size="small"
            testId="guest-pass-popup"
            className="ia-pass-popup"
            onClose={onClose}
            data-m6-layout="modals"
            data-m6-component="EventGuestPassPopup"
            actions={confirming ? (
                <>
                    <Button3D onClick={() => setConfirming(false)} disabled={busy}>Keep this pass</Button3D>
                    <Button3D variant="go" onClick={replace} disabled={busy}>{busy ? 'Making…' : 'Make a new pass'}</Button3D>
                </>
            ) : (
                <>
                    <Button3D onClick={onClose}>Close</Button3D>
                    {pass && canReplace && <Button3D onClick={() => setConfirming(true)}>Replace pass</Button3D>}
                    {pass && <Button3D onClick={() => downloadPass(pass)}>Download</Button3D>}
                    {pass && <Button3D variant="go" onClick={() => printPasses()}>Print</Button3D>}
                </>
            )}
        >
            {state.loading && <p className="py-4 text-center">Loading…</p>}
            {state.error && <p role="alert" className="ia-resv-error">{state.error}</p>}
            {pass && !state.loading && (
                <>
                    <PassCard pass={pass} event={state.data} qrSize={200} />
                    <PassPrintSheet passes={[pass]} event={state.data} />
                </>
            )}
            {confirming && <p role="alert" className="mt-3 text-sm font-semibold" data-testid="pass-replace-warning">The old pass stops working at the door straight away. Use this if a pass was lost or shared.</p>}
            {notice && <p role="status" className="mt-3 text-sm font-semibold" data-testid="pass-replaced">{notice}</p>}
        </Modal3D>
    );
}

export function PassSheetPopup({ client, engagementId, words, onClose }) {
    usePrintCleanup();
    const [state] = usePasses(client, engagementId, null);
    const passes = state.data?.passes || [];
    return (
        <Modal3D
            portal
            title="QR passes"
            subtitle={`One pass per guest and plus-one for this ${words?.eventLower || 'event'}. Print the sheet, cut and hand out.`}
            size="wide"
            testId="pass-sheet-popup"
            onClose={onClose}
            data-m6-layout="modals"
            data-m6-component="EventPassSheetPopup"
            actions={(
                <>
                    <Button3D onClick={onClose}>Close</Button3D>
                    <Button3D variant="go" onClick={() => printPasses()} disabled={passes.length === 0}>{`Print ${passes.length} ${passes.length === 1 ? 'pass' : 'passes'}`}</Button3D>
                </>
            )}
        >
            {state.loading && <p className="py-4 text-center">Loading…</p>}
            {state.error && <p role="alert" className="ia-resv-error">{state.error}</p>}
            {!state.loading && !state.error && passes.length === 0 && <p className="py-4 text-center">No guests yet. Add guests to make their passes.</p>}
            {passes.length > 0 && (
                <>
                    <div className="ia-pass-sheet" data-testid="pass-sheet">
                        {passes.map((pass) => <PassCard key={pass.guestId} pass={pass} event={state.data} qrSize={132} />)}
                    </div>
                    <PassPrintSheet passes={passes} event={state.data} />
                </>
            )}
        </Modal3D>
    );
}
